import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import "./CustomDropdown.css";
import { Search, ChevronDown } from "lucide-react";
import FallbackImg from "../../components/FallbackImg";

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "",
  className = "",
  renderOption, // optional custom render function
  disabled = false,
  isMulti = false,
  popupRef,
  hideSearchbar = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const [openUpward, setOpenUpward] = useState(false);
  const menuRef = useRef(null);

  const filteredOptions = options.filter((opt) => {
    if (!search) return true;
    if (typeof opt.label === "string") {
      return opt.label.toLowerCase().includes(search.toLowerCase());
    }
    if (typeof opt.value === "string") {
      return opt.value.toLowerCase().includes(search.toLowerCase());
    }
    return false;
  });

  // Measure the ACTUAL rendered menu height and recompute position
  // before the browser paints, so there's no flicker.
  useLayoutEffect(() => {
    if (!open || !ref.current) return;

    const recalcPosition = () => {
      if (!ref.current) return;

      const triggerRect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      // Use the actual rendered menu height if available,
      // otherwise fall back to a reasonable estimate for the first frame.
      const menuHeight = menuRef.current
        ? menuRef.current.getBoundingClientRect().height
        : 220;

      const buffer = 8; // small safety margin from viewport edge

      if (spaceBelow < menuHeight + buffer && spaceAbove > spaceBelow) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    };

    recalcPosition();

    window.addEventListener("resize", recalcPosition);
    window.addEventListener("scroll", recalcPosition, true); // capture scroll on any ancestor

    return () => {
      window.removeEventListener("resize", recalcPosition);
      window.removeEventListener("scroll", recalcPosition, true);
    };
  }, [open, search, options.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popupRef && popupRef.current && popupRef.current.contains(e.target))
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popupRef]);

  const selected = options.find((opt) => opt.value === value);

  const isOptionSelected = (opt) => {
    if (isMulti && Array.isArray(value)) {
      return value.some(
        (v) => JSON.stringify(v.value) === JSON.stringify(opt.value),
      );
    }

    // For single select, compare value objects by content
    // return JSON.stringify(opt.value) === JSON.stringify(value.value);

    // For single select, compare directly if value is a string
    return value === opt.value;
  };

  // Disable browser scrolling when the dropdown is open
  useEffect(() => {
    if (open && !disabled) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, disabled]);

  return (
    <>
      {open && !disabled && (
        <div
          className="custom-dropdown-overlay_v2"
          onClick={() => setOpen(false)}
        />
      )}
      <div className={`custom-dropdown-container_v2 ${className}`} ref={ref}>
        <div
          className={`custom-dropdown-selected_v2 ${className} ${
            disabled ? "disabled_v2" : ""
          }`}
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
        >
          <div>
            {isMulti
              ? Array.isArray(value) && value.length > 0
                ? value.map((v, idx) => (
                    <span
                      key={JSON.stringify(v.value)}
                      className="custom-dropdown-multi-tag_v2"
                    >
                      {renderOption ? renderOption(v) : v.label}
                      {value.length > 1 && idx < value.length - 1 ? ", " : ""}
                    </span>
                  ))
                : placeholder
              : selected
                ? renderOption
                  ? renderOption(selected)
                  : selected.label
                : placeholder}
          </div>
          <ChevronDown
            size={16}
            className={open ? "rotate_v2" : ""}
            style={disabled ? { opacity: 0.5 } : {}}
          />
        </div>
        {open && !disabled && (
          <div
            ref={menuRef}
            className={`custom-dropdown-menu_v2 ${className} ${
              openUpward ? "open-upward_v2" : ""
            }`}
          >
            {!hideSearchbar && (
              <div className="searchbar-container-customdropdown_v2">
                <div className="searchbar-customdropdown_v2">
                  <Search size={16} color={"#a1a1a1"} />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search"
                    autoFocus
                  />
                </div>
              </div>
            )}
            <div style={{ padding: "8px 0" }}>
              {filteredOptions.map((opt) => {
                return (
                  <div
                    key={opt.value}
                    className={`custom-dropdown-item_v2${
                      isOptionSelected(opt) ? " selected_v2" : ""
                    }`}
                    onClick={() => {
                      if (disabled) return;
                      if (isMulti) {
                        let newValue = Array.isArray(value) ? [...value] : [];
                        if (isOptionSelected(opt)) {
                          newValue = newValue.filter(
                            (v) => v.value !== opt.value,
                          );
                        } else {
                          newValue.push(opt);
                        }
                        onChange(newValue, opt);
                      } else {
                        onChange(opt.value, opt);
                        setOpen(false);
                      }
                    }}
                  >
                    <span>{renderOption ? renderOption(opt) : opt.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CustomDropdown;
